import { stringToColor, unwrapConnection } from "@utils/utils";

import { ClassificationSubClassEnum } from "@api/graphql/types-and-hooks";
import { MediaCardType } from "@pages/VideoSessionsV2/components/MediaCards";
import {
  MediaTimelineCategory,
  MediaTimelineEvent,
} from "@pages/VideoSessionsV2/components/MediaTimeline/MediaTimeline";
import {
  GoalMoment,
  GoalMomentMediaTargetPrediction,
  TargetMoment,
  VideoMomentType,
} from "@providers/VideoSessionProviderV2";

export const getTickInterval = (pixelsPerSecond: number): number => {
  const minSpacing = 60; // min px between labels

  const niceSteps = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800]; // up to 30 minute for labels

  for (const step of niceSteps) {
    const spacing = step * pixelsPerSecond;

    if (spacing >= minSpacing) {
      return step;
    }
  }
  return niceSteps[niceSteps.length - 1];
};

const NICE_STEPS = [
  1,
  2,
  5,
  10,
  15,
  30, // seconds
  60,
  120,
];

export const getMajorInterval = (pixelsPerSecond: number, duration: number): number => {
  const minSpacing = 100; // px between verticals
  const minTicks = 3; // ensure at least this many
  const maxTicks = 12; // avoid too many

  for (const step of NICE_STEPS) {
    const spacing = step * pixelsPerSecond;
    const tickCount = Math.ceil(duration / step);

    if (spacing >= minSpacing && tickCount >= minTicks && tickCount <= maxTicks) {
      return step;
    }
  }

  return NICE_STEPS[NICE_STEPS.length - 1];
};

export const getBaseVisibleSeconds = (duration: number): number => {
  if (duration <= 60) return duration; // show full clip if tiny
  if (duration <= 120) return 60; // ~1m visible for <2m videos
  if (duration <= 600) return 150; // ~2m30s for <10m videos
  if (duration <= 3600) return 300; // ~5m for <1h videos
  if (duration <= 10800) return 900; // ~15m for <3h videos
  return 1800; // ~30m for very long videos
};

export const getColorForGoal = (goalName: string): string => {
  return stringToColor(goalName, 60, 60);
};

// Filter moments to those within media duration
const filterMomentsWithinDuration = (moments: GoalMomentMediaTargetPrediction[], mediaDuration: number) => {
  return moments.filter((mpt) => mpt.startTime < mediaDuration && mpt.endTime > 0);
};

// Condense overlapping or enclosed moments into single spans
const getAggregateMoments = (
  goalMoments: GoalMomentMediaTargetPrediction[],
  targets: TargetMoment[],
  mediaDuration: number,
): MediaTimelineEvent[] => {
  const targetMoments = targets.flatMap((target) => unwrapConnection(target.mediatargetpredictionSet) || []);
  const filteredTargetMoments = filterMomentsWithinDuration(targetMoments, mediaDuration);
  const allMoments = [...goalMoments, ...filteredTargetMoments].sort((a, b) => a.startTime - b.startTime);

  const aggregated: MediaTimelineEvent[] = [];
  let currentStart: number | null = null;
  let currentEnd: number | null = null;

  for (const moment of allMoments) {
    if (currentStart === null || currentEnd === null) {
      currentStart = moment.startTime;
      currentEnd = moment.endTime;
      continue;
    }
    // Merge if overlapping or enclosed
    if (moment.startTime <= currentEnd) {
      currentEnd = Math.max(currentEnd, moment.endTime);
    } else {
      aggregated.push({
        id: `moment-${currentStart}-${currentEnd}`,
        start: currentStart,
        end: currentEnd,
        color: "", // Color can be set later
      });
      currentStart = moment.startTime;
      currentEnd = moment.endTime;
    }
  }
  if (currentStart !== null && currentEnd !== null) {
    aggregated.push({
      id: `moment-${currentStart}-${currentEnd}`,
      start: currentStart,
      end: currentEnd,
      color: "",
    });
  }

  return aggregated;
};

export const getMomentsDataForTimeline = (goals: GoalMoment[], mediaDuration: number): MediaTimelineCategory[] => {
  const momentsData: MediaTimelineCategory[] = [];

  goals.forEach((goal) => {
    const goalMoments = filterMomentsWithinDuration(
      unwrapConnection(goal.mediatargetpredictionSet) || [],
      mediaDuration,
    ); // uncategorized moments
    const goalTargets = unwrapConnection(goal.targets) || [];

    if (goalMoments.length === 0 && goalTargets.length === 0) {
      return; // Do not include goal if no targets or moments
    }

    const color = getColorForGoal(goal.name || "no goal name");
    const aggregateMoments = getAggregateMoments(goalMoments, goalTargets, mediaDuration);

    momentsData.push({
      id: goal.id,
      title: goal.name!,
      isPinned: goal.isPinned,
      events: aggregateMoments.map((mpt) => ({ ...mpt, color, aggregated: true, goalId: goal.id })),
      children: [
        {
          id: `${goal.id}-uncategorized`,
          title: "Uncategorized",
          events: goalMoments.map((mpt) => {
            return {
              id: mpt.id,
              start: mpt.startTime,
              end: mpt.endTime,
              color,
              goalId: goal.id,
            };
          }),
        },
        ...goalTargets.map((target) => {
          const targetMoments = filterMomentsWithinDuration(
            unwrapConnection(target.mediatargetpredictionSet) || [],
            mediaDuration,
          );

          return {
            id: target.id,
            title: target.name || "unknown target name",
            events:
              targetMoments.map((mpt) => ({
                id: mpt.id,
                start: mpt.startTime,
                end: mpt.endTime,
                color,
                goalId: goal.id,
                targetId: target.id,
              })) || [],
          };
        }),
      ],
    });
  });

  return momentsData;
};

export const getRowBackground = (color: string, greyedOut: boolean) => {
  if (greyedOut) {
    const colorWithHsla = color.replace("hsl(", "hsla(");
    const colorWithOpacity = colorWithHsla.replace(")", ", 0.5)");
    const colorWithMoreOpacity = colorWithHsla.replace(")", ", 0.3)");
    const colorStripeWidth = 10;
    const negativeStripeWidth = 3;
    return {
      background: `repeating-linear-gradient(
          66deg,
          ${colorWithOpacity},     
          ${colorWithOpacity} ${colorStripeWidth}px,
          ${colorWithMoreOpacity} ${colorStripeWidth}px, 
          ${colorWithMoreOpacity} ${colorStripeWidth + negativeStripeWidth}px 
        )`,
    };
  }

  return {
    backgroundColor: color,
  };
};

export const convertMediaCardInteractionsToVideoMoments = (
  media: MediaCardType,
  isOtherMaladaptiveEnabled: boolean,
): VideoMomentType[] => {
  const allInteractions = [
    ...(media.mediaClassifications?.continuousInteractions || []),
    ...(media.mediaClassifications?.nonContinuousInteractions || []),
  ];

  return allInteractions
    .filter((item) => {
      if (isOtherMaladaptiveEnabled) {
        return true;
      }
      return item?.subclass !== ClassificationSubClassEnum.Aggression;
    })
    .flatMap((interaction) =>
      (interaction?.intervals || []).map((interval) => {
        const startTime = interval?.startTime || 0;
        const endTime = interval?.endTime || 0;
        const confidenceLevel = interval?.confidenceLevel || 0;
        const momentId = `${interaction?.subclass}-${startTime}-${endTime}-${confidenceLevel}`;
        return {
          id: momentId,
          startTime,
          endTime,
          confidenceLevel,
          type: interaction?.subclass as ClassificationSubClassEnum,
        };
      }),
    )
    .sort((a, b) => a.startTime - b.startTime);
};
